'use client';

import type { ReactNode } from 'react';

import Image from 'next/image';

import { ArrowRight, CheckCircle2, MinusCircle } from 'lucide-react';

import { MessagingCtaButtons } from '@/components/contact/MessagingCtaButtons';
import { UnifiedContainer } from '@/components/layout/UnifiedContainer';
import { SectionHeading } from '@/components/marketing/SectionHeading';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import { Button } from '@/components/ui/button';
import { Link } from '@/i18n/routing';

export function ServiceBreadcrumbs({ items }: { items: { label: string; href?: string }[] }) {
  return (
    <UnifiedContainer size='xl' padding='lg' className='pt-6'>
      <Breadcrumbs items={items} size='sm' />
    </UnifiedContainer>
  );
}

export function DetailSection({
  eyebrow,
  title,
  intro,
  id,
  children,
  tone = 'light',
}: {
  eyebrow?: string;
  title: ReactNode;
  intro?: string;
  id?: string;
  children: ReactNode;
  tone?: 'light' | 'dark';
}) {
  const dark = tone === 'dark';
  return (
    <section
      id={id}
      className={dark ? 'bg-slate-950 py-12 sm:py-16' : 'bg-white py-12 sm:py-16 scroll-mt-24'}
    >
      <UnifiedContainer size='xl' padding='lg'>
        {eyebrow ? (
          <p
            className={
              dark
                ? 'text-xs font-semibold uppercase tracking-[0.2em] text-orange-200'
                : 'text-xs font-semibold uppercase tracking-[0.2em] text-slate-500'
            }
          >
            {eyebrow}
          </p>
        ) : null}
        <SectionHeading className={dark ? 'mt-2 text-left text-white' : 'mt-2 text-left'}>
          {title}
        </SectionHeading>
        {intro ? (
          <p
            className={
              dark
                ? 'mt-3 max-w-3xl leading-relaxed text-white/70'
                : 'mt-3 max-w-3xl leading-relaxed text-muted-foreground'
            }
          >
            {intro}
          </p>
        ) : null}
        <div className='mt-8'>{children}</div>
      </UnifiedContainer>
    </section>
  );
}

export function IncludedChecklist({
  groups,
  notIncludedTitle,
  notIncluded,
  tone = 'light',
}: {
  groups: { title: string; items: string[] }[];
  notIncludedTitle?: string;
  notIncluded?: string[];
  tone?: 'light' | 'dark';
}) {
  const dark = tone === 'dark';
  const card = dark
    ? 'rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6'
    : 'rounded-2xl border border-slate-200 bg-slate-50/60 p-5 sm:p-6';
  const titleCls = dark ? 'text-lg font-bold text-white' : 'text-lg font-bold text-foreground';
  const itemCls = dark
    ? 'flex items-start gap-2.5 text-sm leading-relaxed text-white/75'
    : 'flex items-start gap-2.5 text-sm leading-relaxed text-muted-foreground';
  return (
    <div className='grid gap-6 md:grid-cols-2'>
      {groups.map(group => (
        <div key={group.title} className={card}>
          <h3 className={titleCls}>{group.title}</h3>
          <ul className='mt-4 space-y-2.5'>
            {group.items.map(item => (
              <li key={item} className={itemCls}>
                <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-orange-400' aria-hidden />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {notIncluded && notIncluded.length > 0 ? (
        <div
          className={
            dark
              ? 'rounded-2xl border border-white/10 bg-transparent p-5 sm:p-6 md:col-span-2'
              : 'rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 md:col-span-2'
          }
        >
          <h3 className={titleCls}>{notIncludedTitle}</h3>
          <ul className='mt-4 grid gap-2.5 sm:grid-cols-2'>
            {notIncluded.map(item => (
              <li key={item} className={itemCls}>
                <MinusCircle className='mt-0.5 h-4 w-4 shrink-0 text-slate-400' aria-hidden />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function MethodologySteps({
  steps,
  tone = 'light',
}: {
  steps: { title: string; description: string }[];
  tone?: 'light' | 'dark';
}) {
  if (steps.length === 0) {
    return null;
  }
  const dark = tone === 'dark';
  return (
    <ol className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
      {steps.map((step, index) => (
        <li
          key={step.title}
          className={
            dark
              ? 'rounded-2xl border border-white/10 bg-white/5 p-5'
              : 'rounded-2xl border border-slate-200 bg-white p-5'
          }
        >
          <div className='flex items-center gap-3'>
            <span
              className={
                dark
                  ? 'flex h-8 w-8 items-center justify-center rounded-full bg-orange-peel-500 text-sm font-bold text-white'
                  : 'flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white'
              }
              aria-hidden
            >
              {index + 1}
            </span>
            <h3
              className={
                dark ? 'text-base font-bold text-white' : 'text-base font-bold text-foreground'
              }
            >
              {step.title}
            </h3>
          </div>
          <p
            className={
              dark
                ? 'mt-3 text-sm leading-relaxed text-white/70'
                : 'mt-3 text-sm leading-relaxed text-muted-foreground'
            }
          >
            {step.description}
          </p>
        </li>
      ))}
    </ol>
  );
}

export function BulletList({
  items,
  tone = 'light',
}: {
  items: string[];
  tone?: 'light' | 'dark';
}) {
  const dark = tone === 'dark';
  return (
    <ul className='grid gap-2.5 sm:grid-cols-2'>
      {items.map(item => (
        <li
          key={item}
          className={
            dark
              ? 'flex items-start gap-2.5 text-sm leading-relaxed text-white/80'
              : 'flex items-start gap-2.5 text-sm leading-relaxed text-muted-foreground'
          }
        >
          <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-orange-400' aria-hidden />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function PriceFactors({
  factors,
  note,
  tone = 'light',
}: {
  factors: string[];
  note?: string;
  tone?: 'light' | 'dark';
}) {
  const dark = tone === 'dark';
  return (
    <div
      className={
        dark
          ? 'rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-8'
          : 'rounded-2xl border border-slate-200 bg-slate-50/60 p-5 sm:p-8'
      }
    >
      <ul className='grid gap-2.5 sm:grid-cols-2'>
        {factors.map(factor => (
          <li
            key={factor}
            className={
              dark
                ? 'flex items-start gap-2.5 text-sm leading-relaxed text-white'
                : 'flex items-start gap-2.5 text-sm leading-relaxed text-foreground'
            }
          >
            <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-orange-400' aria-hidden />
            <span>{factor}</span>
          </li>
        ))}
      </ul>
      {note ? (
        <p
          className={
            dark
              ? 'mt-4 text-sm leading-relaxed text-white/70'
              : 'mt-4 text-sm leading-relaxed text-muted-foreground'
          }
        >
          {note}
        </p>
      ) : null}
    </div>
  );
}

export function BookingSteps({
  steps,
  primaryLabel,
  primaryHref,
  tone = 'light',
}: {
  steps: { title: string; description: string }[];
  primaryLabel: string;
  primaryHref: string;
  tone?: 'light' | 'dark';
}) {
  const dark = tone === 'dark';
  return (
    <div>
      <ol className='grid gap-4 sm:grid-cols-2 lg:grid-cols-5'>
        {steps.map((step, index) => (
          <li
            key={step.title}
            className={
              dark
                ? 'rounded-2xl border border-white/10 bg-white/5 p-4'
                : 'rounded-2xl border border-slate-200 bg-white p-4'
            }
          >
            <span
              className={
                dark
                  ? 'text-xs font-bold uppercase tracking-widest text-orange-200'
                  : 'text-xs font-bold uppercase tracking-widest text-muted-foreground'
              }
            >
              {String(index + 1).padStart(2, '0')}
            </span>
            <h3
              className={
                dark
                  ? 'mt-1 text-sm font-bold text-white'
                  : 'mt-1 text-sm font-bold text-foreground'
              }
            >
              {step.title}
            </h3>
            <p
              className={
                dark
                  ? 'mt-1 text-xs leading-relaxed text-white/65'
                  : 'mt-1 text-xs leading-relaxed text-muted-foreground'
              }
            >
              {step.description}
            </p>
          </li>
        ))}
      </ol>
      <div className='mt-6'>
        <Button
          asChild
          size='lg'
          className={
            dark
              ? 'min-h-12 rounded-full bg-orange-peel-500 px-7 text-white hover:bg-orange-peel-600'
              : 'min-h-11 rounded-full'
          }
        >
          <Link href={primaryHref}>
            {primaryLabel}
            <ArrowRight className='ml-2 h-4 w-4' aria-hidden />
          </Link>
        </Button>
      </div>
    </div>
  );
}

export function RelatedServices({
  services,
  contextLine,
  contextLinkLabel,
  contextHref,
}: {
  services: { slug: string; title: string; description: string; image: string }[];
  contextLine?: string;
  contextLinkLabel?: string;
  contextHref?: string;
}) {
  if (services.length === 0) {
    return null;
  }
  return (
    <div>
      {contextLine ? (
        <p className='mb-6 max-w-3xl text-sm leading-relaxed text-muted-foreground'>
          {contextLine}{' '}
          {contextLinkLabel && contextHref ? (
            <Link
              href={contextHref}
              className='font-medium text-foreground underline underline-offset-4'
            >
              {contextLinkLabel}
            </Link>
          ) : null}
        </p>
      ) : null}
      <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
        {services.map(service => (
          <Link
            key={service.slug}
            href={`/services/${service.slug}`}
            className='group overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow hover:shadow-lg'
          >
            <div className='relative aspect-[16/9] w-full overflow-hidden bg-slate-100'>
              <Image
                src={service.image}
                alt={service.title}
                fill
                sizes='(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'
                className='object-cover transition-transform duration-300 group-hover:scale-105'
                loading='lazy'
              />
            </div>
            <div className='p-4'>
              <h3 className='text-base font-bold text-foreground group-hover:underline'>
                {service.title}
              </h3>
              <p className='mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground'>
                {service.description}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ServiceFinalCta({
  title,
  subtitle,
  primaryLabel,
  primaryHref,
  whatsappLabel,
  whatsappAria,
  contactLabel,
  pricingLabel,
}: {
  title: string;
  subtitle: string;
  primaryLabel: string;
  primaryHref: string;
  whatsappLabel: string;
  whatsappAria: string;
  contactLabel: string;
  pricingLabel: string;
}) {
  return (
    <section className='bg-slate-950 py-14 sm:py-20'>
      <UnifiedContainer size='xl' padding='lg'>
        <div className='mx-auto max-w-3xl text-center'>
          <h2 className='text-3xl font-extrabold tracking-tight text-white md:text-4xl'>{title}</h2>
          <p className='mx-auto mt-4 max-w-2xl leading-relaxed text-white/80'>{subtitle}</p>
          <div className='mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row'>
            <Button
              asChild
              size='lg'
              className='min-h-11 bg-white text-slate-950 hover:bg-white/90'
            >
              <Link href={primaryHref}>
                {primaryLabel}
                <ArrowRight className='ml-2 h-4 w-4' aria-hidden />
              </Link>
            </Button>
            <MessagingCtaButtons
              whatsappLabel={whatsappLabel}
              whatsappAria={whatsappAria}
              buttonClassName='border-white/40 bg-transparent text-white hover:bg-white hover:text-slate-950'
              whatsappClassName='border-transparent bg-[#25D366] text-white hover:bg-[#1fb857] hover:text-white'
              viberClassName='border-transparent bg-[#7360F2] text-white hover:bg-[#5f4de0] hover:text-white'
            />
          </div>
          <p className='mt-6 text-sm text-white/70'>
            <Link
              href='/contact'
              className='font-medium text-white underline-offset-4 hover:underline'
            >
              {contactLabel}
            </Link>
            <span className='mx-3 text-white/40' aria-hidden>
              ·
            </span>
            <Link
              href='/pricing'
              className='font-medium text-white underline-offset-4 hover:underline'
            >
              {pricingLabel}
            </Link>
          </p>
        </div>
      </UnifiedContainer>
    </section>
  );
}
